/**
 * ⟦A-16⟧ **Le récapitulatif des jours écoulés — montrer le calcul, jamais le solde.**
 *
 * Mot de Mathieu : *« Il ne faut pas que le user se dise : je suis censé perdre 10 points mais
 * en fait j'en perds que 7. Parce qu'il ne voit pas qu'il en perd 10 et qu'il en regagne 3
 * grâce aux nuits imposées à l'intérieur. Il faut bien décrire tout le calcul. »*
 *
 * Depuis ⟦A-14⟧ une longue coûte **48 h** et un evergreen **64 h** : le joueur clique une fois
 * et le monde avance de quatre jours. Charges débitées, tranches de courbes versées, rente,
 * tendances qui changent de phase, idées qui se périment ⟦A-15 §1⟧, rival qui monte
 * ⟦A-15 §3⟧, opportunités qui se referment ⟦A-15 §4⟧, et quatre mauvaises nuits à +3.
 * **Un solde n'explique rien** ; « −10 le travail, +3 tes nuits » explique tout, en une fois,
 * sans tutoriel.
 *
 * ── Ce que ce module N'EST PAS ─────────────────────────────────────────────────
 *
 * Ce n'est ni une règle, ni un calcul, ni une source de vérité. **L'information existe
 * déjà** — la file §2.3 résout les frontières de jour une à une —, elle était simplement
 * agrégée trop tôt. Ce module ne fait que **la laisser remonter**.
 *
 * D'où le parti pris : les lignes sont **MESURÉES sur l'état**, jamais re-dérivées. On prend
 * un instantané des jauges, on laisse l'étape s'exécuter, on compare. Un récapitulatif ne peut
 * donc pas mentir ni diverger d'une formule qui bougerait : s'il affiche −10, c'est que l'état
 * a perdu 10. C'est aussi ce qui évite de faire passer une « cause » à travers dix modules.
 *
 * ⚠️ **Ne pas réutiliser l'agrégation du journal.** `texteDeNuit` (`sleep.ts`) résume
 * volontairement une nuit en une ligne, « sinon dormir trois jours noie le journal ». Ici
 * c'est l'inverse : **le détail EST le sujet** (A-16).
 *
 * ── L'état neutre par défaut (CLAUDE.md, ⟦A-10⟧) ────────────────────────────────
 *
 * Aucune jauge n'est touchée, aucun tirage RNG n'est consommé, aucun ordre n'est modifié :
 * le récapitulatif est **purement observationnel**. `HookContext.recap` est optionnel — un
 * module qui l'ignore ne contribue simplement aucune ligne, et les tests qui montent la file
 * à la main continuent de passer sans rien changer.
 *
 * Pureté (CLAUDE.md) : aucune I/O, aucune horloge système, aucun `Math.random`.
 */
import { jour } from "./clock.js";
// ─────────────────────────────────────────────────────────────────────────────
// Vocabulaires fermés
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Les jauges suivies. Ce sont celles que le joueur VOIT (PLAN §4.2), plus `xp`/`pc` qui
 * expliquent une montée de niveau, plus `inspiration` (nombre de cartes) — sans quoi
 * « ton idée s'est périmée » n'aurait pas de contrepartie chiffrée.
 */
export const RECAP_JAUGES = [
    "abonnes",
    "energie",
    "sante_mentale",
    "reputation",
    "fidelite",
    "argent",
    "momentum",
    "inspiration",
    "xp",
    "pc",
    /**
     * ⟦SPEC-FIL §9.4 · M3.23⟧ **La Hype, huitième jauge.** Elle entre ici parce qu'elle est la
     * **cinquième pression du temps** ⟦A-15⟧ — le monde continue sans toi — et qu'une pression
     * qu'on ne voit pas n'existe pas ⟦A-16⟧ : sa décroissance DOIT apparaître dans le
     * récapitulatif de toute action qui traverse une nuit, comme le decay de momentum.
     *
     * `undefined ⇔ 0` : une run sans Fil mesure 0 avant et 0 après, donc ne pousse aucune ligne
     * — la table ne liste jamais des zéros. L'état neutre est gratuit.
     */
    "hype",
];
/**
 * ⟦A-81⟧ L'instantané, exposé pour le récapitulatif de délégation : l'avant / après de chaque
 * jauge se lit avec **la même fonction** que les lignes ⟦A-16⟧, sinon les deux diverg(er)aient
 * au premier `undefined ⇔ 0`.
 */
export function instantaneJauges(state) {
    return instantane(state);
}
function instantane(state) {
    return {
        abonnes: state.subscribers,
        energie: state.energy,
        sante_mentale: state.mental,
        reputation: state.reputation,
        fidelite: state.loyalty,
        argent: state.money,
        momentum: state.momentum,
        inspiration: state.inspiration.length,
        xp: state.xp,
        pc: state.pc,
        // ⟦SPEC-FIL §2.1⟧ Jamais lue en direct ailleurs ; ici l'instantané n'a pas le droit
        // d'importer `fil.ts` (qui importe `events.ts`, qui importe tout le reste), et
        // `undefined ⇔ 0` est la seule règle à connaître.
        hype: state.hype ?? 0,
    };
}
/** Un collecteur branché sur le clone de travail de la file §2.3. */
export function nouveauRecap(state) {
    const lignes = [];
    /** Jour attribué aux lignes en cours — `null` ⇒ celui de `clock_h`. Voir `auJour`. */
    let jourForce = null;
    const pousser = (cause, jauge, delta, detail) => {
        lignes.push({
            ordre: lignes.length,
            clock_h: state.clock_h,
            jour: jourForce ?? jour(state.clock_h),
            cause,
            jauge,
            delta,
            detail,
        });
    };
    return {
        mesurer(cause, fn, detail = null) {
            const avant = instantane(state);
            const depuis = lignes.length;
            const r = fn();
            const apres = instantane(state);
            // ⟦A-16 · A-82⟧ **La mesure d'ensemble ne rattrape que ce qu'aucune ligne fine n'explique.**
            // Tout ce qui a été posé PENDANT `fn` — par `ligne()` ou par un `mesurer()` imbriqué —
            // porte déjà sa cause ; le retirer ici est ce qui fait de « la nuit » ou de « l'effet de
            // l'action » un reste, et non un doublon. Sans cette soustraction, chaque ligne fine était
            // comptée deux fois (mesuré : `momentum_silence·fidelite·−2` ET `nuit·fidelite·−2`, même
            // nuit, même jauge), et le solde d'en-tête — qui additionne tout — mentait d'autant.
            const expliques = {};
            for (let i = depuis; i < lignes.length; i++) {
                const l = lignes[i];
                if (l.jauge !== null)
                    expliques[l.jauge] = (expliques[l.jauge] ?? 0) + l.delta;
            }
            for (const jaugeNom of RECAP_JAUGES) {
                const delta = apres[jaugeNom] - avant[jaugeNom] - (expliques[jaugeNom] ?? 0);
                // Les jauges fractionnaires (momentum, argent) : on ne pousse pas un bruit d'arrondi.
                if (Math.abs(delta) < 1e-9)
                    continue;
                pousser(cause, jaugeNom, delta, detail);
            }
            return r;
        },
        ligne(cause, jauge, delta, detail = null) {
            if (Math.abs(delta) < 1e-9)
                return;
            pousser(cause, jauge, delta, detail);
        },
        fait(cause, detail = null) {
            pousser(cause, null, 0, detail);
        },
        auJour(n, fn) {
            const precedent = jourForce;
            jourForce = n;
            try {
                return fn();
            }
            finally {
                jourForce = precedent;
            }
        },
        rendre(clockDebut, joursTraverses) {
            return {
                jours_traverses: joursTraverses,
                clock_debut: clockDebut,
                clock_fin: state.clock_h,
                lignes,
            };
        },
    };
}
// ─────────────────────────────────────────────────────────────────────────────
// Lectures pour l'UI
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Le **solde** par jauge — pour l'en-tête du panneau. ⚠️ Il ne remplace jamais les lignes :
 * A-16 interdit explicitement de ne montrer que ça (« un solde n'explique rien »). Il sert à
 * écrire « Santé mentale : −7 » **au-dessus** du détail qui le décompose, pas à la place.
 */
export function soldeParJauge(recap) {
    const total = {};
    for (const l of recap.lignes) {
        if (l.jauge === null)
            continue;
        total[l.jauge] = (total[l.jauge] ?? 0) + l.delta;
    }
    return total;
}
/**
 * Les lignes groupées par **jour simulé croissant**, l'ordre du moteur conservé à l'intérieur
 * de chaque jour — la structure de l'écran d'A-16.
 *
 * ⚠️ **Ce groupement était CONSÉCUTIF, et il mentait sur données réelles.** Il ouvrait un
 * groupe dès que le jour changeait d'une ligne à l'autre ; mesuré sur une vraie longue, la
 * suite des jours vaut `J3, J6, J4, J5, J6` — les nuits contenues sont datées de l'arrivée
 * (`repos.ts` s'applique après l'avancée d'horloge) et les idées périmées sont purgées AVANT
 * la boucle des frontières. Le helper produisait donc des en-têtes « Jour 6 » puis « Jour 4 »,
 * ce qu'A-16 interdit en toutes lettres : *« ligne à ligne, dans l'ordre chronologique »*.
 *
 * C'est la raison pour laquelle `client/src/game/recap.ts` avait dû écrire son propre
 * `parJourCroissant` plutôt que d'appeler celui-ci. Il peut désormais l'appeler.
 *
 * Aucun delta n'est touché : c'est de la mise en page.
 */
export function recapParJour(recap) {
    const parJour = new Map();
    for (const l of recap.lignes) {
        const groupe = parJour.get(l.jour);
        if (groupe)
            groupe.push(l);
        else
            parJour.set(l.jour, [l]);
    }
    return [...parJour.entries()]
        .sort((a, b) => a[0] - b[0])
        .map(([jourDuGroupe, lignes]) => ({
        jour: jourDuGroupe,
        lignes: [...lignes].sort((a, b) => a.ordre - b.ordre),
    }));
}
//# sourceMappingURL=recap.js.map